// ─── SUPPORT ───────────────────────────────────────────────────
const SUPPORT_CATEGORIES = [
  'Source connection',
  'CSV import',
  'Orders',
  'Export / 3PL',
  'Billing',
  'Account',
  'Other',
];

const SUPPORT_STATUS_META = {
  open: { label:'Open', color:'#1D4ED8', bg:'#DBEAFE', Icon: MessageSquare },
  waiting_on_support: { label:'Waiting on support', color:'#92400E', bg:'#FEF3C7', Icon: Clock3 },
  waiting_on_user: { label:'Waiting on you', color:'#166534', bg:'#DCFCE7', Icon: UserCircle },
  resolved: { label:'Resolved', color:'#374151', bg:'#F3F4F6', Icon: CircleCheck },
};

const supportRemovalTitle = (status, removesFromInboxAt) => {
  if (status !== 'resolved' || !removesFromInboxAt) return undefined;
  const parsed = new Date(String(removesFromInboxAt).replace(' ', 'T') + 'Z');
  if (Number.isNaN(parsed.getTime())) return 'Resolved tickets are removed from inboxes after 48 hours, but remain stored on the server.';
  const ms = parsed.getTime() - Date.now();
  if (ms <= 0) return 'This resolved ticket will be removed from inboxes soon, but remains stored on the server.';
  const hours = Math.floor(ms / 3600000);
  const minutes = Math.ceil((ms % 3600000) / 60000);
  const label = hours >= 1 ? `${hours} hour${hours === 1 ? '' : 's'}${minutes ? ` ${minutes} minute${minutes === 1 ? '' : 's'}` : ''}` : `${minutes} minute${minutes === 1 ? '' : 's'}`;
  return `Removed from inboxes in ${label}. Ticket data remains stored on the server.`;
};

const SupportStatusBadge = ({ status, adminMode = false, removesFromInboxAt }) => {
  const base = SUPPORT_STATUS_META[status] || SUPPORT_STATUS_META.open;
  const c = adminMode && status === 'waiting_on_user' ? { ...base, label:'Waiting on customer' } : base;
  const Icon = c.Icon;
  return <span title={supportRemovalTitle(status, removesFromInboxAt)} style={{ padding:'3px 8px', borderRadius:4, background:c.bg, color:c.color, fontSize:11, fontWeight:800, whiteSpace:'nowrap', display:'inline-flex', alignItems:'center', gap:5 }}><Icon size={11} strokeWidth={2.4}/>{c.label}</span>;
};

const getSupportCategoryIcon = (category = '') => {
  if (/source/i.test(category)) return Upload;
  if (/csv/i.test(category)) return FileSpreadsheet;
  if (/order/i.test(category)) return Package;
  if (/export|3pl/i.test(category)) return Truck;
  if (/billing/i.test(category)) return DollarSign;
  if (/account/i.test(category)) return UserCircle;
  return LifeBuoy;
};

const SUPPORT_FILE_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif', 'application/pdf', 'text/plain', 'text/csv'];
const readSupportFiles = (files) => Promise.all(Array.from(files || []).slice(0, 4).map(file => new Promise((resolve, reject) => {
  if (!SUPPORT_FILE_TYPES.includes(file.type)) return reject(new Error('Attach PNG, JPEG, WebP, GIF, PDF, TXT, or CSV files.'));
  if (file.size > 2 * 1024 * 1024) return reject(new Error('Each file must be 2 MB or smaller.'));
  const reader = new FileReader();
  reader.onload = () => resolve({ fileName: file.name || `pasted-image-${Date.now()}.png`, mimeType: file.type, dataUrl: reader.result, byteSize: file.size });
  reader.onerror = () => reject(new Error('Could not read file.'));
  reader.readAsDataURL(file);
})));

const SupportComposer = ({ placeholder, submitLabel, onSubmit, initialMessage = '', embedded = false }) => {
  const [message, setMessage] = useState(initialMessage);
  const [attachments, setAttachments] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (initialMessage) setMessage(initialMessage);
  }, [initialMessage]);

  const addFiles = async files => {
    setError('');
    try {
      const next = await readSupportFiles(files);
      setAttachments(prev => [...prev, ...next].slice(0, 4));
    } catch (err) {
      setError(err.message || 'Could not attach file.');
    }
  };

  const attach = async e => {
    await addFiles(e.target.files);
    e.target.value = '';
  };

  const paste = async e => {
    const files = Array.from(e.clipboardData?.items || [])
      .filter(item => item.kind === 'file')
      .map(item => item.getAsFile())
      .filter(Boolean);
    if (!files.length) return;
    e.preventDefault();
    await addFiles(files);
  };

  const send = async e => {
    e.preventDefault();
    if (!message.trim()) {
      setError('Add a message first.');
      return;
    }
    setSaving(true);
    setError('');
    const result = await onSubmit({ message: message.trim(), attachments });
    setSaving(false);
    if (result?.error) {
      setError(result.error);
      return;
    }
    setMessage('');
    setAttachments([]);
  };

  return (
    <form onSubmit={send} className={`support-composer${embedded ? ' is-embedded' : ''}`}>
      {embedded && <label className="support-field-label">How can we help?</label>}
      <div className="support-composer-box">
        <textarea value={message} onChange={e => setMessage(e.target.value)} onPaste={paste} placeholder={placeholder} className="support-composer-textarea"/>
        {attachments.length > 0 && (
          <div style={{ display:'flex', gap:8, flexWrap:'wrap', padding:'0 10px 8px' }}>
            {attachments.map((a, i) => (
              <div key={`${a.fileName}-${i}`} style={{ display:'flex', alignItems:'center', gap:7, border:'1px solid #E5E7EB', borderRadius:7, padding:'5px 7px', background:'#F9FAFB', maxWidth:180 }}>
                {a.mimeType.startsWith('image/')
                  ? <img src={a.dataUrl} alt="" style={{ width:28, height:28, objectFit:'cover', borderRadius:4, flexShrink:0 }}/>
                  : <span style={{ width:28, height:28, borderRadius:4, background:'#EEF4FF', color:'#006FDD', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}><Paperclip size={14}/></span>
                }
                <span title={a.fileName} style={{ fontSize:11, color:'#374151', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{a.fileName}</span>
                <button type="button" onClick={() => setAttachments(prev => prev.filter((_, idx) => idx !== i))} aria-label="Remove attachment" style={{ border:'none', background:'transparent', cursor:'pointer', color:'#9CA3AF', padding:0, display:'flex' }}><X size={13}/></button>
              </div>
            ))}
          </div>
        )}
        <div className="support-composer-toolbar">
          <label title="Attach files" className="support-attach-button">
            <Paperclip size={14}/><span>Attach files</span>
            <input type="file" accept="image/png,image/jpeg,image/webp,image/gif,application/pdf,text/plain,text/csv" multiple onChange={attach} style={{ display:'none' }}/>
          </label>
          <div className="support-composer-toolbar-end">
            <div className="support-attachment-count">PNG, JPG, PDF, TXT or CSV · {attachments.length}/4</div>
            {!embedded && (
              <button type="submit" disabled={saving} className="support-primary-button">
                {saving ? 'Sending...' : submitLabel} <Send size={14}/>
              </button>
            )}
          </div>
        </div>
      </div>
      {error && <div style={{ color:'#DC2626', fontSize:12, marginTop:8 }}>{error}</div>}
      {embedded && (
        <div className="support-composer-actions">
          <button type="submit" disabled={saving} className="support-primary-button">
            {saving ? 'Sending...' : submitLabel} <Send size={14}/>
          </button>
        </div>
      )}
    </form>
  );
};

const SupportScreen = ({ user, screen, orders = [], products = [], adminMode = false }) => {
  const [tickets, setTickets] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [creating, setCreating] = useState(false);
  const [category, setCategory] = useState(SUPPORT_CATEGORIES[0]);
  const [subject, setSubject] = useState('');
  const [orderId, setOrderId] = useState('');
  const [includeDiagnostics, setIncludeDiagnostics] = useState(true);
  const [draftMessage, setDraftMessage] = useState('');
  const [error, setError] = useState('');

  const input = { width:'100%', border:'1px solid #E5E7EB', borderRadius:8, padding:'9px 11px', fontSize:13, outline:'none', background:'#fff' };
  const label = { fontSize:11, fontWeight:800, color:'#6B7280', marginBottom:6, display:'block', letterSpacing:'0.05em', textTransform:'uppercase' };
  const sourceCounts = orders.reduce((acc, order) => {
    const source = order.source || 'unknown';
    acc[source] = (acc[source] || 0) + 1;
    return acc;
  }, {});
  const refreshAdminSidebarSummary = () => {
    if (adminMode) window.dispatchEvent(new Event('omnifill:admin-summary-refresh'));
  };

  const loadTickets = useCallback(async () => {
    const { ok, data } = await api('GET', `/api/support/tickets?scope=${adminMode ? 'admin' : 'mine'}`);
    if (ok) {
      setTickets(data);
      if (!activeId && data.length) setActiveId(data[0].id);
      if (!data.length && !adminMode) setCreating(true);
    }
  }, [activeId, adminMode]);

  const loadDetail = useCallback(async (id) => {
    if (!id) return;
    const { ok, data } = await api('GET', `/api/support/tickets/${id}`);
    if (ok) setDetail(data);
  }, []);

  useEffect(() => { loadTickets(); }, []);
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('omnifill_support_draft');
      if (!raw) return;
      const draft = JSON.parse(raw);
      sessionStorage.removeItem('omnifill_support_draft');
      if (draft.category && SUPPORT_CATEGORIES.includes(draft.category)) setCategory(draft.category);
      if (draft.subject) setSubject(String(draft.subject).slice(0, 160));
      if (draft.message) setDraftMessage(String(draft.message));
      setCreating(true);
    } catch {
      try { sessionStorage.removeItem('omnifill_support_draft'); } catch {}
    }
  }, []);
  useEffect(() => { if (activeId) loadDetail(activeId); }, [activeId, loadDetail]);
  useEffect(() => {
    if (adminMode && creating) setCreating(false);
  }, [adminMode, creating]);

  const submitNewTicket = async ({ message, attachments }) => {
    setError('');
    if (!subject.trim() || !message.trim()) {
      setError('Add a subject and message so support has enough context.');
      return { error: 'Add a subject and message so support has enough context.' };
    }
    const diagnostics = {
      screen,
      path: window.location.pathname,
      orderCount: orders.length,
      productCount: products.length,
      sourceCounts,
      connectedSources: {
        stripe: !!user?.hasStripeConnected,
        paypal: !!user?.hasPayPalConnected,
        shopify: !!user?.hasShopifyConnected,
        woocommerce: !!user?.hasWooCommerceConnected,
        adobe_commerce: !!user?.hasAdobeCommerceConnected,
      },
      viewport: { width: window.innerWidth, height: window.innerHeight },
      timestamp: new Date().toISOString(),
    };
    const { ok, data } = await api('POST', '/api/support/tickets', {
      category,
      subject: subject.trim(),
      message,
      orderId: orderId.trim() || undefined,
      includeDiagnostics,
      diagnostics,
      pageUrl: window.location.href,
      userAgent: navigator.userAgent,
      attachments,
    });
    if (!ok) {
      return { error: data.error || 'Could not create ticket' };
    }
    setSubject('');
    setOrderId('');
    setDraftMessage('');
    setCreating(false);
    await loadTickets();
    setActiveId(data.ticketId);
    return { ok: true };
  };

  const submitReply = async ({ message, attachments }) => {
    if (!detail?.ticket?.id) return { error: 'Choose a ticket first.' };
    const { ok, data } = await api('POST', `/api/support/tickets/${detail.ticket.id}/messages`, { message, attachments, asAdmin: adminMode });
    if (!ok) return { error: data.error || 'Could not send reply' };
    await loadTickets();
    await loadDetail(detail.ticket.id);
    refreshAdminSidebarSummary();
    return { ok: true };
  };

  const updateStatus = async status => {
    if (!detail?.ticket?.id) return;
    const { ok } = await api('PATCH', `/api/support/tickets/${detail.ticket.id}`, { status });
    if (ok) {
      await loadTickets();
      await loadDetail(detail.ticket.id);
      refreshAdminSidebarSummary();
    }
  };

  const displayTicketNumber = ticket => ticket?.ticketNumber ? `#${ticket.ticketNumber}` : '#------';

  const deleteResolvedTicket = async () => {
    if (!adminMode || detail?.ticket?.status !== 'resolved') return;
    if (!confirm(`Remove resolved ticket ${displayTicketNumber(detail.ticket)} from the inbox? It will remain stored on the server.`)) return;
    const deletedId = detail.ticket.id;
    const { ok, data } = await api('DELETE', `/api/support/tickets/${deletedId}`);
    if (!ok) {
      setError(data.error || 'Could not delete ticket');
      return;
    }
    setTickets(prev => prev.filter(ticket => ticket.id !== deletedId));
    setActiveId(null);
    setDetail(null);
    refreshAdminSidebarSummary();
  };

  const activeTicket = detail?.ticket;

  return (
    <div className="dash-page workspace-page support-page">
      <div className="support-workspace">
        <div className="support-ticket-rail">
          <div className="support-rail-header">
            <div>
              <div className="support-rail-title">Support</div>
              <div className="support-rail-subtitle">{tickets.length ? `${tickets.length} ticket${tickets.length === 1 ? '' : 's'}` : 'Your conversations'}</div>
            </div>
            {!adminMode && (
              <button className="support-new-icon-button" title="New ticket" aria-label="New ticket" onClick={() => { setCreating(true); setDetail(null); }}>
                <Plus size={17}/>
              </button>
            )}
          </div>
          <div style={{ flex:1, minHeight:0, overflowY:'auto' }}>
            {tickets.length === 0 ? (
              <div className="support-empty-rail">
                <span className="support-empty-icon"><MessageSquare size={19}/></span>
                <strong>No conversations yet</strong>
                <span>When you contact us, your replies will stay organized here.</span>
              </div>
            ) : tickets.map(ticket => {
              const active = !creating && activeId === ticket.id;
              const TicketIcon = getSupportCategoryIcon(ticket.category);
              return (
                <button key={ticket.id} className={`support-ticket-row${active ? ' is-active' : ''}`} onClick={() => { setCreating(false); setActiveId(ticket.id); loadDetail(ticket.id); }}>
                  <div style={{ display:'flex', justifyContent:'space-between', gap:8, alignItems:'flex-start', marginBottom:7 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8, minWidth:0 }}>
                      <span style={{ width:26, height:26, borderRadius:7, background:active ? '#E8F1FF' : '#F3F4F6', color:active ? '#006FDD' : '#6B7280', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}><TicketIcon size={14} strokeWidth={2}/></span>
                      <div style={{ fontSize:13, fontWeight:800, color:'#1A1A2E', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{ticket.subject}</div>
                    </div>
                    <SupportStatusBadge status={ticket.status} adminMode={adminMode} removesFromInboxAt={ticket.removesFromInboxAt}/>
                  </div>
                  {adminMode && <div style={{ fontSize:11, color:'#6B7280', marginBottom:5 }}>{ticket.user?.email}</div>}
                  <div style={{ fontSize:12, color:'#6B7280', lineHeight:1.4, display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden' }}>{ticket.lastMessage || ticket.category}</div>
                  <div style={{ display:'flex', justifyContent:'space-between', gap:8, marginTop:8, fontSize:11, color:'#9CA3AF', alignItems:'center' }}>
                    <span style={{ display:'inline-flex', alignItems:'center', gap:4 }}><MessageSquare size={11}/>{displayTicketNumber(ticket)}</span>
                    <span style={{ display:'inline-flex', alignItems:'center', gap:4 }}>
                      {ticket.attachmentCount > 0 && <><Paperclip size={11}/>{ticket.attachmentCount}</>}
                      <Clock3 size={11}/>{timeAgo(parseOrderSeenDate({ created_at: ticket.lastMessageAt }))}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
          {!adminMode && (
            <div className="support-rail-footer">
              <button onClick={() => { setCreating(true); setDetail(null); }} className="support-new-button">
                <Plus size={14}/> New ticket
              </button>
            </div>
          )}
        </div>

        <div className="support-chat-panel">
          {creating && !adminMode ? (
            <div className="support-new-ticket">
              <div className="support-new-ticket-inner">
                <div className="support-new-heading">
                  <span className="support-new-heading-icon"><LifeBuoy size={21}/></span>
                  <div>
                    <h1>How can we help?</h1>
                    <p>Share the details below and our support team will take a look.</p>
                  </div>
                </div>
                <div className="support-response-note">
                  <Clock3 size={15}/>
                  <span>Add the key steps and a screenshot when possible so we can investigate faster.</span>
                </div>
                <div className="support-form-card">
                  <div className="support-form-grid">
                    <div>
                      <label style={label}>Category</label>
                      <select value={category} onChange={e => setCategory(e.target.value)} style={input}>
                        {SUPPORT_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={label}>Order ID <span className="support-optional">optional</span></label>
                      <input value={orderId} onChange={e => setOrderId(e.target.value)} style={input} placeholder="ABC-1234-DEFG"/>
                    </div>
                  </div>
                  <div>
                    <label style={label}>Subject</label>
                    <input value={subject} onChange={e => setSubject(e.target.value)} style={input} placeholder="A short summary of the issue"/>
                  </div>
                  <SupportComposer placeholder="Tell us what happened, what you expected, and any steps we can use to reproduce it." submitLabel="Send to support" initialMessage={draftMessage} onSubmit={submitNewTicket} embedded/>
                  <label className="support-diagnostics">
                    <input type="checkbox" checked={includeDiagnostics} onChange={e => setIncludeDiagnostics(e.target.checked)}/>
                    <span>
                      <span className="support-diagnostics-title">Include diagnostic information</span>
                      <span className="support-diagnostics-copy">Shares your current screen, browser, connected sources, and order and product counts. Never includes passwords or payment details.</span>
                    </span>
                  </label>
                </div>
              </div>
            </div>
          ) : activeTicket ? (
            <>
              <div style={{ padding:'16px 18px', borderBottom:'1px solid #E5E7EB', display:'flex', justifyContent:'space-between', gap:12, alignItems:'flex-start' }}>
                <div>
                  <div style={{ display:'flex', alignItems:'center', gap:9, flexWrap:'wrap' }}>
                    <h2 style={{ fontSize:18, fontWeight:900, color:'#1A1A2E', margin:0 }}>{activeTicket.subject}</h2>
                    <SupportStatusBadge status={activeTicket.status} adminMode={adminMode} removesFromInboxAt={activeTicket.removesFromInboxAt}/>
                  </div>
                  <div style={{ fontSize:12, color:'#6B7280', marginTop:5 }}>
                    {displayTicketNumber(activeTicket)} · {activeTicket.category}{activeTicket.orderId ? ` · Order ${activeTicket.orderId}` : ''}{adminMode ? ` · ${activeTicket.user?.email}` : ''}
                  </div>
                </div>
                <div style={{ display:'flex', gap:8, flexWrap:'wrap', justifyContent:'flex-end' }}>
                  {activeTicket.status !== 'resolved' ? (
                    <button onClick={() => updateStatus('resolved')} style={{ background:'#F3F4F6', color:'#374151', border:'none', borderRadius:8, padding:'8px 10px', fontSize:12, fontWeight:800, cursor:'pointer' }}>Mark resolved</button>
                  ) : (
                    <>
                      <button onClick={() => updateStatus('open')} style={{ background:'#EEF4FF', color:'#006FDD', border:'none', borderRadius:8, padding:'8px 10px', fontSize:12, fontWeight:800, cursor:'pointer' }}>Reopen</button>
                      {adminMode && <button onClick={deleteResolvedTicket} style={{ background:'#FEF2F2', color:'#DC2626', border:'none', borderRadius:8, padding:'8px 10px', fontSize:12, fontWeight:800, cursor:'pointer', display:'inline-flex', alignItems:'center', gap:6 }}><Trash2 size={13}/> Delete</button>}
                    </>
                  )}
                </div>
              </div>
              <div className="support-message-canvas">
                {(detail.messages || []).map(msg => {
                  const mine = adminMode ? msg.authorRole === 'admin' : msg.authorRole === 'user';
                  return (
                    <div key={msg.id} className={`support-message-row${mine ? ' is-mine' : ''}`}>
                      <div className="support-message-wrap">
                        <div className="support-message-meta">{msg.authorName} · {timeAgo(parseOrderSeenDate({ created_at: msg.createdAt }))}</div>
                        <div className={`support-message-bubble${mine ? ' is-mine' : ''}`}>{msg.message}</div>
                        {msg.attachments?.length > 0 && (
                          <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginTop:8, justifyContent:mine ? 'flex-end' : 'flex-start' }}>
                            {msg.attachments.map(a => (
                              <a key={a.id} href={a.dataUrl} target="_blank" rel="noreferrer" style={{ display:'block', border:'1px solid #E5E7EB', borderRadius:7, overflow:'hidden', background:'#fff' }} title={a.fileName}>
                                {String(a.mimeType || '').startsWith('image/')
                                  ? <img src={a.dataUrl} alt={a.fileName} style={{ display:'block', width:116, height:86, objectFit:'cover' }}/>
                                  : <span style={{ width:116, height:86, display:'flex', flexDirection:'column', gap:6, alignItems:'center', justifyContent:'center', color:'#006FDD', fontSize:11, fontWeight:800, padding:8, boxSizing:'border-box' }}><Paperclip size={20}/><span style={{ maxWidth:'100%', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{a.fileName}</span></span>
                                }
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              <SupportComposer placeholder="Write a reply..." submitLabel="Send reply" onSubmit={submitReply}/>
            </>
          ) : (
            <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', color:'#9CA3AF', fontSize:13 }}>{adminMode ? 'Choose a ticket.' : 'Choose or create a ticket.'}</div>
          )}
        </div>
      </div>
    </div>
  );
};
